import Script from 'next/script'
import { Constants } from '@/assets'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { getFirstQueryParam, getQueryParam } from '@/utils'
import { UrlParamType } from '@/modules/utils'
import useCurrentUser from '@/hooks/useCurrentUser'
import { MessageEventData_ScreenView } from '@/data'
import { useDeviceDetector } from '@/hooks'

declare global {
  interface Window {
    dataLayer: Record<string, any>[]
  }
}

type GTMValue = string | number | boolean | Record<string, any> | Array<Record<string, any>>

export const gtmSendCustomEvent = (name: string, params: Record<string, GTMValue>) => {
  if (Constants.gTagId) {
    window.dataLayer?.push({
      event: name,
      ...params,
    })
  }
}

export const gtmPageView = (params: {
  pageUrl: string
  pageTitle: string
  userId?: string
  userEmail?: string
  userRole?: string
  userFirstName?: string
  userLastName?: string
  appType?: string
}) => {
  if (Constants.gTagId) {
    window.dataLayer?.push({
      event: 'PageView',
      brand: Constants.storageID,
      ...params,
    })
  }
}

export const GTMScript = () => {
  return (
    Constants.gTagId && (
      <Script id="google-tag-manager" strategy="afterInteractive">
        {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${Constants.gTagId}');`}
      </Script>
    )
  )
}

export const useGTMPageView = (pageTitle: string) => {
  const router = useRouter()
  const { currentUnknownUser } = useCurrentUser()
  const { appType } = useDeviceDetector()

  useEffect(() => {
    const queryType = getFirstQueryParam(router.query.type)

    /**
     * ⚠️ if 'customer-id' is present in query params, don't send GTM event
     * We use this key for "floating" profile and we need to send GTM event manually (see search page for example)
     */
    if (
      (!currentUnknownUser ||
        queryType === UrlParamType.Owner ||
        queryType === UrlParamType.Borrower ||
        queryType === UrlParamType.Pro) &&
      !getQueryParam(router.asPath, 'customer-id')
    ) {
      gtmPageView({
        pageUrl: router.asPath,
        pageTitle,
        userId: currentUnknownUser?.id,
        userRole:
          queryType === UrlParamType.Owner
            ? 'maitre'
            : queryType === UrlParamType.Borrower
            ? 'emprunteur'
            : queryType === UrlParamType.Pro
            ? 'pro'
            : undefined,
        appType: appType,
      })
    }

    // Event webview screen view
    if (typeof window !== 'undefined' && typeof window.ReactNativeWebView !== 'undefined' && currentUnknownUser) {
      const eventData: MessageEventData_ScreenView = {
        event: 'screenView',
        screenName: `m_${pageTitle}`,
        screenClass: `m_${router.asPath}`,
      }

      // Post the message
      window.ReactNativeWebView.postMessage(JSON.stringify(eventData))
    }
  }, [router.asPath, currentUnknownUser])
}
